import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import sum from 'lodash/sum'

import {
  useFetchSubscriptionsStatisticCountQuery,
} from 'api/dashboard'

import colors from 'components/utils/colors'
import StatsCounterCard from './StatsCounterCard/Index'
import StatsChartDataCard from './StatsChartDataCard/Index'

const countersTypes = [
  'recentBondedTicketsCount',
  'recentUnbondedTicketsCount',
  'newTicketsCount',
  'closedTicketsCount',
  'activeTicketsWithSubscriptions',
  'subscribedUsers',
]
const chartsDataTypes = ['unbonded', 'bonded', 'closed', 'open']

const StyledWrapper = styled.div`
  display: flex;
  height: 104px;
  box-sizing: border-box;
  background-color: #f4f5f6;
  padding: 8px 16px 8px 16px;
`

const StatsPanel = ({
  ticketingSystemDisplayId, isSummaryTab, colorizedBondedUnbondedTickets,
  colorizedTicketStatuses, recentBondedTicketsCount, recentUnbondedTicketsCount, recentNewTicketsCount,
  recentClosedTicketsCount, fetchingBondedUnbonded, fetchingOpenClosed, fetchingRecentBondedUnbonded,
  fetchingRecentNewClosed,
}) => {
  const { t } = useTranslation()

  /* eslint-disable key-spacing, no-multi-spaces */
  const paramsByType = {
    'unbonded':                   { title: t('dashboardPage.unbonded'), icon: 'icon-status-unbonded' },
    'bonded':                     { title: t('dashboardPage.bonded'),   icon: 'icon-status-bonded' },
    'closed':                     { title: t('dashboardPage.closed'),   icon: 'icon-status-closed' },
    'open':                       { title: t('dashboardPage.open'),     icon: 'icon-status-inprogress' },
    'recentUnbondedTicketsCount': { title: t('dashboardPage.unbonded'), icon: 'icon-status-unbonded' },
    'recentBondedTicketsCount':   { title: t('dashboardPage.bonded'),   icon: 'icon-status-bonded' },
    'closedTicketsCount':         { title: t('dashboardPage.closed'),   icon: 'icon-status-closed' },
    'newTicketsCount':            { title: t('dashboardPage.created'),  icon: 'icon-status-new' },
    'activeTicketsWithSubscriptions': {
      title: t('dashboardPage.activeTickets'),
      subtitle: `(${t('dashboardPage.withSubscriptions')})`,
      icon: 'icon-primary',
    },
    'subscribedUsers': {
      title: t('dashboardPage.activeUsers'),
      subtitle: `(${t('dashboardPage.withSubscriptions')})`,
      icon: 'icon-multi-user',
    },
  }
  /* eslint-enable key-spacing, no-multi-spaces */

  const statsTypes = isSummaryTab ? countersTypes : chartsDataTypes

  const StatsCard = isSummaryTab ? StatsCounterCard : StatsChartDataCard

  const { data, isFetching } =
    useFetchSubscriptionsStatisticCountQuery({
      ticketingSystemDisplayId: ticketingSystemDisplayId,
    },
    {
      skip: !isSummaryTab,
    })

  const activeTicketsWithSubscriptions = data?.activeTicketsWithSubscriptions
  const subscribedUsers = data?.subscribedUsers
  const fetchingSubscribtionsStatistic = isFetching

  const dataStatistic = {
    'unbonded': { data: colorizedBondedUnbondedTickets.find(i => i.name === 'unbonded'), fetching: fetchingBondedUnbonded },
    'bonded': { data: colorizedBondedUnbondedTickets.find(i => i.name === 'bonded'), fetching: fetchingBondedUnbonded },
    'closed': { data: colorizedTicketStatuses.find(i => i.name === 'closed'), fetching: fetchingOpenClosed },
    'open': { data: colorizedTicketStatuses.find(i => i.name === 'open'), fetching: fetchingOpenClosed },
    'recentBondedTicketsCount': { data: recentBondedTicketsCount, fetching: fetchingRecentBondedUnbonded },
    'recentUnbondedTicketsCount': { data: recentUnbondedTicketsCount, fetching: fetchingRecentBondedUnbonded },
    'newTicketsCount': { data: recentNewTicketsCount, fetching: fetchingRecentNewClosed },
    'closedTicketsCount': { data: recentClosedTicketsCount, fetching: fetchingRecentNewClosed },
    'activeTicketsWithSubscriptions': { data: activeTicketsWithSubscriptions, fetching: fetchingSubscribtionsStatistic },
    'subscribedUsers': { data: subscribedUsers, fetching: fetchingSubscribtionsStatistic },
  }

  return (
    <StyledWrapper>
      {statsTypes.map(statsType => {
        const params = paramsByType[statsType]
        if (!params) {
          console.warn(`StatsPanel: statsType="${statsType}" doesn't exist`)
          return undefined
        }

        const value = isSummaryTab ? dataStatistic[statsType].data : sum(dataStatistic[statsType].data?.data)
        const { title, subtitle, icon } = params

        return (
          <StatsCard
            key={statsType}
            statsType={statsType}
            title={title}
            subtitle={subtitle}
            color={colors.secondary20}
            icon={icon}
            chartData={dataStatistic[statsType].data}
            value={value}
            fetching={dataStatistic[statsType].fetching}
          />
        )
      })}
    </StyledWrapper>
  )
}

StatsPanel.propTypes = {
  isSummaryTab: PropTypes.bool,
  ticketingSystemDisplayId: PropTypes.string,
  colorizedBondedUnbondedTickets: PropTypes.array,
  colorizedTicketStatuses: PropTypes.array,
  recentBondedTicketsCount: PropTypes.number,
  recentUnbondedTicketsCount: PropTypes.number,
  recentNewTicketsCount: PropTypes.number,
  recentClosedTicketsCount: PropTypes.number,
  fetchingBondedUnbonded: PropTypes.bool,
  fetchingOpenClosed: PropTypes.bool,
  fetchingRecentBondedUnbonded: PropTypes.bool,
  fetchingRecentNewClosed: PropTypes.bool,
}

export default StatsPanel
